import { createContext, useContext, useState, useEffect } from 'react';

// Graphvine portal theme system.
// The marketing site is dark-only; the portal supports both for a11y / preference.
const THEMES = ['graphvine-dark', 'graphvine-light'];

// Kept for compatibility with anything that still references COLORWAYS —
// the new design has a single emerald accent, so this is a no-op list with
// just one entry that won't tint anything.
export const COLORWAYS = {
  emerald: { rotate: 0, swatch: '#10b981' },
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem('gv-theme');
    if (stored && THEMES.includes(stored)) return stored;
    // Migrate from old key
    const legacy = localStorage.getItem('gs-theme');
    if (legacy === 'jarvis') return 'graphvine-dark';
    return 'graphvine-dark';
  });

  // Always a single colorway now — keep for API compatibility
  const [colorway, setColorwayState] = useState('emerald');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Tailwind dark mode class
    if (theme === 'graphvine-dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('gv-theme', theme);
  }, [theme]);

  const setTheme = (t) => {
    if (THEMES.includes(t)) setThemeState(t);
  };
  const setColorway = (c) => {
    if (COLORWAYS[c]) setColorwayState(c);
  };
  const toggleMode = () => {
    setThemeState(t => (t === 'graphvine-dark' ? 'graphvine-light' : 'graphvine-dark'));
  };

  return (
    <ThemeContext.Provider value={{
      theme, setTheme, themes: THEMES,
      colorway, setColorway,
      toggleMode,
      isDark: theme === 'graphvine-dark',
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
